/**
 * Friendly / legacy aliases → canonical network icon kind.
 */
import type { NetworkIconKind } from './types';

export const NETWORK_ALIASES: Record<string, NetworkIconKind> = {
  // Infrastructure
  internet: 'internet',
  cloud: 'cloud',
  router: 'router',
  core_router: 'router',
  edge_router: 'router',
  switch: 'switch',
  core_switch: 'switch',
  distribution_switch: 'switch',
  access_switch: 'switch',
  hub: 'hub',
  bridge: 'bridge',
  repeater: 'repeater',
  gateway: 'gateway',
  modem: 'modem',
  wireless_controller: 'wlc',
  wlc: 'wlc',
  wireless_access_point: 'wap',
  access_point: 'wap',
  ap: 'wap',
  wap: 'wap',
  mesh_node: 'mesh',
  mesh: 'mesh',
  network_extender: 'extender',
  extender: 'extender',

  // Security
  security: 'firewall',
  firewall: 'firewall',
  next_generation_firewall: 'ngfw',
  ngfw: 'ngfw',
  web_application_firewall: 'waf',
  waf: 'waf',
  vpn_gateway: 'vpn',
  vpn: 'vpn',
  ids: 'ids',
  intrusion_detection_system: 'ids',
  ips: 'ips',
  intrusion_prevention_system: 'ips',
  proxy_server: 'proxy',
  proxy: 'proxy',
  reverse_proxy: 'reverseProxy',
  secure_web_gateway: 'swg',
  swg: 'swg',
  siem: 'siem',
  nac: 'nac',
  network_access_control: 'nac',

  // Servers
  servers: 'server',
  server: 'server',
  physical_server: 'server',
  virtual_server: 'vm',
  web_server: 'server',
  application_server: 'server',
  database_server: 'database',
  file_server: 'server',
  mail_server: 'server',
  ftp_server: 'server',
  dns_server: 'server',
  dhcp_server: 'server',
  ntp_server: 'server',
  authentication_server: 'server',
  domain_controller: 'server',
  print_server: 'server',
  media_server: 'server',
  backup_server: 'server',

  // Storage
  storage: 'storage',
  nas: 'nas',
  san: 'san',
  storage_array: 'storage',
  object_storage: 'storage',
  backup_storage: 'storage',

  // Endpoints
  end_user_devices: 'desktop',
  client: 'desktop',
  desktop: 'desktop',
  desktop_pc: 'desktop',
  pc: 'desktop',
  laptop: 'laptop',
  workstation: 'workstation',
  thin_client: 'thinClient',
  smartphone: 'phone',
  phone: 'phone',
  mobile: 'phone',
  tablet: 'tablet',
  printer: 'printer',
  scanner: 'scanner',
  ip_phone: 'ipPhone',
  voip_phone: 'ipPhone',
  smart_tv: 'smartTv',

  // Cloud & virtualization
  cloud_virtualization: 'cloud',
  virtual_machine: 'vm',
  vm: 'vm',
  hypervisor: 'hypervisor',
  docker_container: 'container',
  docker: 'container',
  container: 'container',
  kubernetes_cluster: 'k8s',
  kubernetes: 'k8s',
  k8s: 'k8s',
  kubernetes_node: 'k8sNode',
  k8s_node: 'k8sNode',
  api_gateway: 'apiGateway',
  load_balancer: 'loadBalancer',
  lb: 'loadBalancer',
  cdn: 'cdn',
  serverless_function: 'serverless',
  serverless: 'serverless',
  lambda: 'serverless',
  vpc: 'vpc',
  subnet: 'subnet',
  nat_gateway: 'nat',
  nat: 'nat',
  internet_gateway: 'igw',
  igw: 'igw',
  bastion_host: 'bastion',
  bastion: 'bastion',

  // Databases
  databases: 'database',
  database: 'database',
  db: 'database',
  sql_database: 'sql',
  sql: 'sql',
  nosql_database: 'nosql',
  nosql: 'nosql',
  redis_cache: 'redis',
  redis: 'redis',
  cache: 'redis',
  elasticsearch: 'elasticsearch',
  elastic: 'elasticsearch',
  data_warehouse: 'warehouse',
  warehouse: 'warehouse',

  // Messaging
  messaging_integration: 'queue',
  message_queue: 'queue',
  queue: 'queue',
  kafka: 'kafka',
  rabbitmq: 'rabbitmq',
  mqtt_broker: 'mqtt',
  mqtt: 'mqtt',
  event_bus: 'eventBus',
  service_bus: 'serviceBus',

  // Monitoring
  monitoring_management: 'monitor',
  monitoring_server: 'monitor',
  monitor: 'monitor',
  logging_server: 'logging',
  logging: 'logging',
  snmp_manager: 'snmp',
  snmp: 'snmp',
  configuration_server: 'config',
  config_server: 'config',
  network_management_system: 'nms',
  nms: 'nms',

  // IoT
  iot_industrial: 'iotGateway',
  plc: 'plc',
  hmi: 'hmi',
  scada_server: 'scada',
  scada: 'scada',
  rtu: 'rtu',
  opc_ua_server: 'opcua',
  opcua: 'opcua',
  modbus_tcp_device: 'modbus',
  modbus_rtu_device: 'modbus',
  modbus: 'modbus',
  can_gateway: 'canGateway',
  edge_gateway: 'edgeGateway',
  iot_gateway: 'iotGateway',
  esp32: 'esp32',
  raspberry_pi: 'raspberryPi',
  rpi: 'raspberryPi',
  sensor: 'sensor',
  actuator: 'actuator',
  industrial_switch: 'industrialSwitch',

  // Automotive
  automotive: 'ecu',
  ecu: 'ecu',
  can_bus: 'canBus',
  lin_bus: 'linBus',
  flexray: 'flexray',
  ethernet_gateway: 'ethGateway',
  telematics_unit: 'telematics',
  telematics: 'telematics',
  infotainment_system: 'infotainment',
  infotainment: 'infotainment',
  adas_ecu: 'adas',
  adas: 'adas',
  gps_module: 'gps',
  gps: 'gps',
  camera: 'camera',
  radar: 'radar',
  lidar: 'lidar',

  // External
  external_services: 'cloud',
  isp: 'isp',
  dns_provider: 'dnsProvider',
  email_service: 'emailService',
  authentication_provider: 'authProvider',
  auth_provider: 'authProvider',
  payment_gateway: 'payment',
  payment: 'payment',
  third_party_api: 'thirdPartyApi',
  api: 'thirdPartyApi',
  maps_api: 'mapsApi',
  sms_gateway: 'sms',
  sms: 'sms',
  push_notification_service: 'push',
  push: 'push',

  // Links (also usable as nodes in catalogs)
  connections: 'ethernet',
  ethernet: 'ethernet',
  fiber_optic: 'fiber',
  fiber: 'fiber',
  wi_fi: 'wifi',
  wifi: 'wifi',
  vpn_tunnel: 'vpnTunnel',
  mpls: 'mpls',
  sd_wan: 'sdwan',
  sdwan: 'sdwan',
  serial_connection: 'serial',
  serial: 'serial',
  usb: 'usb',
  bluetooth: 'bluetooth',
  zigbee: 'zigbee',
  lorawan: 'lorawan',
  lora: 'lorawan',
  cellular_4g_5g: 'cellular',
  cellular: 'cellular',
  '4g': 'cellular',
  '5g': 'cellular',
  satellite_link: 'satellite',
  satellite: 'satellite',

  // Zones / containers
  common_diagram_containers: 'datacenter',
  data_center: 'datacenter',
  datacenter: 'datacenter',
  office: 'office',
  branch_office: 'branch',
  branch: 'branch',
  dmz: 'dmz',
  lan: 'lan',
  wan: 'wan',
  vlan: 'vlan',
  private_network: 'privateNet',
  private_net: 'privateNet',
  public_network: 'publicNet',
  public_net: 'publicNet',
  cloud_region: 'cloudRegion',

  default: 'default',
};

